import React from "react";
import PropTypes from "prop-types";
import { Utilities } from "@learningblocks/exp-constants";
import { ContentDropdown } from "@learningblocks/shared-components";
import { QuestionWrapper } from "../Components/QuestionWrapper";
import { MainTitle } from "../Components/MainTitle";
import { QuestionImage } from "../Components/QuestionImage";
import { Footer } from "../Components/Footer";
import { TotalAttempts } from "../Components/TotalAttempts";
import { SubmitButton } from "../Components/SubmitButton";
import { Question } from "../Components/Question";
import { MultiSelectFeedback } from "../Components/MultiSelectFeedback";
import { Option } from "./Option";
import "./style.scss";

const { QUESTION_CONTENTS } = Utilities;

/**
 * Experimental-Design Question component [Multi-Select]:
 *  - You will have n choices to select from
 *  - Your question will have m correct otions
 *  - m will be less than n.
 */
export function MultiSelect(props) {
  const hasUnsubmittedOptions = () => {
    return props.checkboxes.reduce((boolVal, checkboxOption) => {
      if (
        checkboxOption.disabled === false &&
        checkboxOption.selected === true
      ) {
        boolVal = true;
      }
      return boolVal;
    }, false);
  };

  const setSubmitDisabled = () => {
    if (props.complete) return true;
    else return hasUnsubmittedOptions() === false;
  };

  const getIsCorrect = ({ id }) => props.answerId.includes(id);

  const onSubmit = () => props.submitAnswer(props.id, props.type);

  const selectOption = (params) =>
    props.selectOption({ ...params, componentID: props.id });

  const renderCheckboxOptions = (key, options) => {
    return (
      <>
        {options.map((option, id) => {
          return (
            <Option
              key={options.id}
              {...option}
              index={id}
              tabIndex={"0"}
              selectOption={selectOption}
              isCorrect={getIsCorrect(option)}
            />
          );
        })}
        <MultiSelectFeedback
          key={key}
          tabIndex={"0"}
          answerId={props.answerId}
          checkboxes={props.checkboxes}
          className="mscq__answerfeedback"
        />
      </>
    );
  };

  const renderImage = (key, image) => (
    <QuestionImage
      tabIndex="0"
      key={key}
      image={image}
      className="mscq__image"
      preview={props.preview}
    />
  );

  const renderQuestion = (key) => (
    <Question
      tabIndex="0"
      key={key}
      text={props.question}
      className="mscq__question"
    />
  );

  const renderText = (key, text) => (
    <Question tabIndex={"0"} key={key} text={text} className="cscq__content" />
  );

  const hintDetailsSection = (key, hintDetails) => {
    return <ContentDropdown key={key} contentDetails={hintDetails} />;
  };

  const renderByType = (type, key, content) => {
    switch (type) {
      case QUESTION_CONTENTS.QUESTION:
        return renderQuestion(key, content);
      case QUESTION_CONTENTS.TEXT:
        return renderText(key, content);
      case QUESTION_CONTENTS.IMAGE:
        return renderImage(key, content);
      case QUESTION_CONTENTS.CHECKBOXES:
        return renderCheckboxOptions(key, props.checkboxes);
      case QUESTION_CONTENTS.HINTS:
        return hintDetailsSection(key, props.hintDetails);
      default:
        return null;
    }
  };

  const renderContents = () =>
    props.content.map((type, key) => renderByType(type, key, props[type]));

  const wrapperClass = ` mscq ${props.className}`;
  return (
    <QuestionWrapper className={wrapperClass}>
      <MainTitle title={props.title} className="mscq__mainTitle" />
      {renderContents()}
      <Footer className="mscq__footer">
        <TotalAttempts
          totalAttempts={props.totalAttempts}
          className="mscq__footer__totalAttempts"
        />
        <SubmitButton disabled={setSubmitDisabled()} onSubmit={onSubmit} />
      </Footer>
    </QuestionWrapper>
  );
}

MultiSelect.propTypes = {
  /**
   * The list of ID's for the correct Options
   */
  answerId: PropTypes.arrayOf(PropTypes.number).isRequired,
  /**
   * The list of options.
   */
  checkboxes: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**
   * The custom classname you wanna inject on the root of the component
   */
  className: PropTypes.string,
  /**
   * The prop to set if the component is complete
   */
  complete: PropTypes.bool.isRequired,
  /**
   * The prop to specify all contents in the question
   */
  content: PropTypes.arrayOf(PropTypes.string).isRequired,
  /**
   * The prop that hold image data
   */
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * The prop that hold the unique id for the component
   */
  id: PropTypes.number || PropTypes.string,
  /**
   * The prop that holds the text for the question
   */
  question: PropTypes.string.isRequired,
  /**
   * The prop that holds the total attempts,
   */
  totalAttempts: PropTypes.number.isRequired,
  /**
   * The prop that holds the Component type.
   */
  type: PropTypes.oneOf(["MSCQ"]),
  /**
   * The callback prop executed when submit is clicked
   */
  submitAnswer: PropTypes.func.isRequired,
  /**
   * The callback to be executed when an option is selected
   */
  selectOption: PropTypes.func.isRequired,
  /**
   * This is the details that are passed to the hint
   */
  hintDetails: PropTypes.shape({
    /**
     * The label that is provided to the Hint section, usually Hint
     */
    label: PropTypes.string.isRequired,
    /**
     * Description details that is given to the hint
     */
    description: PropTypes.string.isRequired,
    /**
     * To show the question title
     */
    title: PropTypes.string,
  }),
  preview: PropTypes.bool,
};

MultiSelect.defaultProps = {
  className: "",
  type: "MSCQ",
  checkboxes: [],
  complete: false,
  content: [],
  image: "",
  question: "",
  totalAttempts: 0,
  answerId: [],
  title: "multi-select question",
  preview: false,
};
